import { useId } from "react";
import { SelectionCard } from "@app/components/base/selection-card/selection-card";
import { LOST_RECEIPT_ESCALATION_LEVEL } from "@app/types/policy";
import type { LostReceiptEscalationLevel } from "@app/types/policy";

export interface LostReceiptProtocolProps {
    /** Currently selected escalation level ("off" when neither option is selected). */
    value: LostReceiptEscalationLevel;
    /** Called when the admin selects (or deselects) an escalation level. */
    onChange: (value: LostReceiptEscalationLevel) => void;
    /** When true, the section is read-only. */
    isDisabled?: boolean;
}

function CheckIcon() {
    return (
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M20 6 9 17l-5-5" />
        </svg>
    );
}

function PencilIcon() {
    return (
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
        </svg>
    );
}

function FlagIcon() {
    return (
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1zM4 22v-7" />
        </svg>
    );
}

/**
 * Molecule: the "Lost Receipt Protocol" section — a mutually-exclusive escalation-level selector
 * (radio-button behavior). "Approver Initial" is always disabled in V1.
 */
export function LostReceiptProtocol({ value, onChange, isDisabled = false }: LostReceiptProtocolProps) {
    const headingId = useId();

    const select = (level: LostReceiptEscalationLevel) => {
        if (isDisabled) return;
        // Selecting the currently active option toggles it back off.
        onChange(value === level ? LOST_RECEIPT_ESCALATION_LEVEL.Off : level);
    };

    return (
        <section aria-labelledby={headingId}>
            <div className="mb-3">
                <h3 id={headingId} className="text-base font-semibold text-gray-900">
                    Lost Receipt Protocol
                </h3>
                <p className="text-sm text-gray-500">Choose escalation requirements for lost receipts.</p>
            </div>

            <div className="flex flex-col gap-2">
                <SelectionCard
                    icon={CheckIcon}
                    label="Initial"
                    description="Require submitter to initial their lost receipt"
                    selected={value === LOST_RECEIPT_ESCALATION_LEVEL.Initial}
                    isDisabled={isDisabled}
                    accent="blue"
                    onSelect={() => select(LOST_RECEIPT_ESCALATION_LEVEL.Initial)}
                />
                <SelectionCard
                    icon={PencilIcon}
                    label="Explanation"
                    description="Require submitter to write an explanation"
                    selected={value === LOST_RECEIPT_ESCALATION_LEVEL.Explanation}
                    isDisabled={isDisabled}
                    accent="amber"
                    onSelect={() => select(LOST_RECEIPT_ESCALATION_LEVEL.Explanation)}
                />
                <SelectionCard
                    icon={FlagIcon}
                    label="Approver Initial"
                    description="Require approver to acknowledge lost receipt"
                    selected={false}
                    isDisabled={true}
                    accent="blue"
                />
            </div>
        </section>
    );
}
